// Apolo Planning Mode — TodoDropdown: the rising panel that shows the approved
// plan's TODOs advancing through reason→act, driven by PlanState (loop/planState.js).
//
// Presentational + prop-driven on purpose: it renders state, never owns it (loop
// engineering: the UI reads the state file). Wiring to the run store is the caller's
// job. Rises from the composer; shad-pro + Cursor accent; honors prefers-reduced-motion;
// keyboard-operable; WCAG-AA labels.

import { Check, Circle, Loader2, AlertTriangle, ChevronDown } from 'lucide-react';
import EnvNoticeCard from './EnvNoticeCard.jsx';

const STATUS_META = {
  pending: { label: 'pendente', Icon: Circle, cls: 'text-apolo-textTertiary' },
  active: { label: 'em andamento', Icon: Loader2, cls: 'text-apolo-accent motion-safe:animate-spin' },
  done: { label: 'concluído', Icon: Check, cls: 'text-apolo-success' },
  blocked: { label: 'bloqueado', Icon: AlertTriangle, cls: 'text-apolo-danger' },
};

// RCA lentidão fix I — linha de progresso VIVO da task ativa ("passo N · tool"),
// alimentada por PlanState.todo_progress (loop_step/tool_call do worker). Antes uma
// task longa era um spinner mudo por 40min; agora dá pra VER o loop andando.
function progressLine(todo) {
  if (todo.status !== 'active' || !todo.progress) return null;
  const { step, tool } = todo.progress;
  const parts = [];
  if (Number.isFinite(Number(step)) && step != null) parts.push(`passo ${step}`);
  if (tool) parts.push(String(tool));
  return parts.length ? parts.join(' · ') : null;
}

function TodoRow({ todo, running = true }) {
  const base = STATUS_META[todo.status] || STATUS_META.pending;
  // O "círculo preso": a task ativa girava (animate-spin) pra sempre quando o run
  // morria. Com o run idle, mantém a forma mas CONGELA (sem giro) — honesto.
  const meta =
    todo.status === 'active' && !running
      ? { ...base, cls: 'text-apolo-textTertiary' }
      : base;
  const { Icon } = meta;
  const progress = progressLine(todo);
  return (
    <li
      className="flex items-start gap-2.5 px-3 py-2 text-sm"
      aria-current={todo.status === 'active' ? 'step' : undefined}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.cls}`} aria-hidden="true" />
      <span
        className={
          todo.status === 'done'
            ? 'text-apolo-textTertiary line-through'
            : todo.status === 'blocked'
              ? 'text-apolo-danger'
              : 'text-apolo-textPrimary'
        }
      >
        {todo.text}
        {progress && (
          <span className="mt-0.5 block truncate font-mono text-xs text-apolo-textTertiary" data-testid="todo-progress">
            {progress}
          </span>
        )}
      </span>
      <span className="sr-only">{`: ${meta.label}`}</span>
    </li>
  );
}

/**
 * @param {{ planState:object, open:boolean, onToggle?:()=>void, onApprove?:()=>void, onReject?:()=>void, running?:boolean }} props
 */
export default function TodoDropdown({ planState, open = false, onToggle, onApprove, onReject, running = true }) {
  const todos = planState?.todos || [];
  const total = todos.length;
  const done = todos.filter((t) => t.status === 'done').length;
  if (!total) return null;

  // Honestidade: "executando" SÓ com o run de fato vivo. executing + run idle
  // (interrompido / DAG abortado) = "pausado" — igual PlanTabView. Sem isso o
  // card mentia "executando" pra sempre depois do run morrer.
  const phaseLabel =
    planState.phase === 'awaiting_approval'
      ? 'aguardando aprovação'
      : planState.phase === 'done'
        ? 'concluído'
        : running
          ? 'executando'
          : 'pausado';

  return (
    <div className="pointer-events-auto w-full select-none">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="apolo-todo-list"
        className="flex w-full items-center justify-between gap-2 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2 text-left text-xs font-medium text-apolo-textSecondary transition hover:bg-apolo-surface2 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
      >
        <span className="flex items-center gap-2">
          <span className="text-apolo-textPrimary">{planState.title || 'Plano'}</span>
          <span className="text-apolo-textTertiary">· {phaseLabel}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="tabular-nums text-apolo-textTertiary">{done}/{total}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </span>
      </button>

      {/* P2 — plano PAUSADO por ambiente (sem node_modules p/ verificar): aviso
          ACIONÁVEL sempre visível (mesmo colapsado), pois é um estado que pede ação. */}
      <EnvNoticeCard outcome={planState.outcome} sessionId={planState.planId} />

      {open && (
        <ul
          id="apolo-todo-list"
          className="mt-1 max-h-64 overflow-y-auto rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1 py-1 shadow-apolo-lg motion-safe:animate-fadeSlide"
          aria-label={`TODOs do plano (${done} de ${total} concluídos)`}
        >
          {todos.map((t) => (
            <TodoRow key={t.id} todo={t} running={running} />
          ))}
          {planState.phase === 'awaiting_approval' && (onApprove || onReject) && (
            <li className="mt-1 flex items-center justify-end gap-2 border-t border-apolo-borderSubtle px-3 pt-2">
              {onReject && (
                <button
                  type="button"
                  onClick={onReject}
                  className="rounded-md border border-apolo-borderSubtle px-2.5 py-1 text-xs font-medium text-apolo-textSecondary transition hover:bg-apolo-surface2 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                >
                  Rejeitar
                </button>
              )}
              {onApprove && (
                <button
                  type="button"
                  onClick={onApprove}
                  className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                >
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  Aprovar e executar
                </button>
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
